/**
 * Is the reader mid-selection inside this log?
 *
 * While they are, the transcript view stops mutating: at a 2s poll, any re-render
 * that touches the selected text collapses the selection, so copying a long quote out
 * of a live conversation is a race the reader keeps losing.
 *
 * Extracted because it is a decision with four ways to be wrong, and the effect it
 * lives in cannot be asserted about — `document.getSelection()` is the browser's, and
 * `selectionchange` fires for carets, drags, keyboard extension and the system's own
 * copy callout alike.
 */
export function selectionHeld(
  log: { contains(node: Node | null): boolean } | null | undefined,
  sel:
    | { isCollapsed: boolean; anchorNode: Node | null; focusNode?: Node | null }
    | null
    | undefined,
): boolean {
  if (!log || !sel) return false
  // A caret is a selection with nothing in it. Holding for one would freeze the view
  // for anyone who merely tapped the text.
  if (sel.isCollapsed) return false
  /*
   * EITHER end, not just the anchor.
   *
   * A selection has two, and iOS lets the drag handles move either one — they swap
   * roles when a handle is dragged past the other. Checking only the anchor releases
   * the hold exactly while someone is widening their selection to take in one more
   * line, which is when they need it held most. avengents-app-builder found this;
   * it is rare on a desktop, where a drag almost always starts inside the log.
   *
   * Asymmetric on purpose: this can only over-hold, never under-hold, and the cost
   * of over-holding is a repaint a few hundred milliseconds late.
   */
  const ends = [sel.anchorNode, sel.focusNode ?? null].filter(Boolean) as Node[]
  if (!ends.length) return false
  // Both ends outside — the composer, the sidebar — is not this log's business, and
  // freezing the conversation because someone selected their own draft would be a
  // stall with no visible cause.
  return ends.some((n) => log.contains(n))
}
